import { computed, DestroyRef, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { I18nParams, TranslationProvider } from '../interfaces';
import { AppLanguage } from '../enums';
import { setActiveDateLocale } from '../utils/date-to-string.util';

const LANG_STORAGE_KEY: string = 'splitzero.lang';

@Injectable({ providedIn: 'root' })
export class TranslationService implements TranslationProvider {
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly currentLang: WritableSignal<AppLanguage> = signal<AppLanguage>(AppLanguage.pl);
  private readonly translationsVersion: WritableSignal<number> = signal<number>(0);

  readonly currentLanguage: Signal<AppLanguage> = computed((): AppLanguage => this.currentLang());

  constructor() {
    this.initialize();
    this.subscribeToTranslationChanges();
  }

  translate(key: string, params?: I18nParams): string {
    return this.translateService.instant(key, params);
  }

  translateSignal(key: string, params?: I18nParams): Signal<string> {
    return computed((): string => {
      this.currentLang();
      this.translationsVersion();
      return this.translateService.instant(key, params);
    });
  }

  /** Bumps every time `onLangChange` / `onTranslationChange` fires - depend on this in computed() so labels rebuild after async JSON load. */
  readonly translationsLoaded: Signal<number> = computed((): number => this.translationsVersion());

  switchLanguage(language: AppLanguage): void {
    this.translateService.use(language);
    this.currentLang.set(language);
    setActiveDateLocale(language);
    try {
      localStorage.setItem(LANG_STORAGE_KEY, language);
    } catch {
      // localStorage may be disabled (Safari private mode etc.) - fail silently
    }
  }

  private subscribeToTranslationChanges(): void {
    const bump: () => void = (): void => {
      this.translationsVersion.update((v: number): number => v + 1);
    };
    const langSub: Subscription = this.translateService.onLangChange.subscribe(bump);
    const trSub: Subscription = this.translateService.onTranslationChange.subscribe(bump);
    this.destroyRef.onDestroy((): void => {
      langSub.unsubscribe();
      trSub.unsubscribe();
    });
  }

  /** True when the user has explicitly picked a language (localStorage entry exists). */
  hasUserPickedLanguage(): boolean {
    try {
      const raw: string | null = localStorage.getItem(LANG_STORAGE_KEY);
      return this.isSupportedLanguage(raw);
    } catch {
      return false;
    }
  }

  private isSupportedLanguage(value: string | null): value is AppLanguage {
    return (
      value === AppLanguage.pl ||
      value === AppLanguage.en ||
      value === AppLanguage.de ||
      value === AppLanguage.cs ||
      value === AppLanguage.fr
    );
  }

  private initialize(): void {
    this.translateService.addLangs([AppLanguage.pl, AppLanguage.en, AppLanguage.de, AppLanguage.cs, AppLanguage.fr]);
    const stored: AppLanguage = this.readStoredLanguage();
    this.translateService.use(stored);
    this.currentLang.set(stored);
    setActiveDateLocale(stored);
  }

  private readStoredLanguage(): AppLanguage {
    try {
      const raw: string | null = localStorage.getItem(LANG_STORAGE_KEY);
      if (this.isSupportedLanguage(raw)) return raw;
    } catch {
      // ignore
    }
    // Default before first user pick: English - onboarding stays in EN until
    // the language picker on first launch.
    return AppLanguage.en;
  }
}
